import { makeAutoObservable, runInAction } from 'mobx'
import orderFactory from './center/orderFactory'
import restaurantCenter from './center/restaurantCenter'
import { storeMobx, cartMobx, RESTAURANT_EVENT } from '..'
import { ddayjs } from '@dt/fe-utils'
import { keyBy, each, sumBy, cloneDeep } from '@dt/fe-utils/dist/lodash'
import OrderAPI from 'api/order.api'

interface IUpdateInfo {
  contactMobile?: string;
  contactName?: string;
  contactInfo?: string;
  tableCode?: string;
  tableAlias?: string;
  person?: number;
  kids?: number;
  ordersId: string;
  tableTent?: string;
  ordersOutNo?: string;
  sourceType?: string;
  addUserName?: string;
}

const restaurantConfig = restaurantCenter.config

const getDefaultOrder = () => {
  return {
    id: 0,
    acceptTime: null,
    addTime: '',
    appType: restaurantConfig.app as any,
    brandId: 0,
    channel: restaurantConfig.app as any,
    cnt: 0,
    currency: 'USD' as 'USD' | 'RMB',
    detailList: [],
    eatType: 'dinein' as 'dinein' | 'delivery' | 'togo' | 'mall',
    modifyTime: restaurantConfig.app === 'pos' ? '' : ddayjs().format(),
    nono: '',
    ordersId: '',
    ordersType: 'dc' as 'dc' | 'cz' | 'goods' | 'mall',
    originalFee: 0,
    paidFee: 0,
    payMode: 0 as 0 | 1,
    person: 0,
    kids: 0,
    remark: '',
    shoule: 0,
    showTax: 0,
    sourceType: '',
    startTime: '',
    status: 'Pending' as 'Success' | 'Pending' | 'NotAccepted' | 'Cancel' | 'Timeout',
    storeId: 0,
    subject: '',
    tip: 0,
    tax: 0,
    freeTax: 0,
    tableAlias: '',
    tableCode: '',
    tableTent: '',
    memberId: '0',
    memberGroupId: '0',
    contactInfo: '{}',
    deliverList: [],
    details: []
  }
}

class Order {
  constructor () {
    makeAutoObservable(this)
  }

  /** 未经加工的订单 */
  order: IOrder = getDefaultOrder()

  /** 锁 */
  $sleep: boolean = false

  /** 订单处理中的状态 */
  doOrder = {
    isLoading: false,
    isSim: false,
    fetchTime: Date.now()
  }

  /** 分单子单 */
  splitInfo = {
    subOrdersId: ''
  }

  /** 批量充值订单 */
  batchRechargeOrder: any = {}

  /** 快递记录 */
  expressMap: Record<string, any> = {}

  /** 加工后的订单级数据 */
  get viewOrder (): Omit<IViewOrder, 'detail'> {
    // 再次加工订单数据用于订单中的附加数据，比如该订单所属门店的信息
    const theStoreId = this.order.storeId
    let store
    if (storeMobx.info.storeId === theStoreId) {
      store = {
        storeName: storeMobx.info.storeName,
        brandName: storeMobx.brand.brandName,
        address: storeMobx.info.address,
        tel: storeMobx.info.tel,
        brandLogo: storeMobx.brand.logo,
        telAreaCode: (storeMobx.info as any).telAreaCode
      }
    } else {
      if (storeMobx.storeList.length) {
        const findStore = storeMobx.storeList.find(store => store.storeId === theStoreId)
        if (findStore) {
          store = {
            storeName: findStore.storeName,
            brandName: storeMobx.brand.brandName,
            address: findStore.address,
            tel: findStore.tel,
            brandLogo: storeMobx.brand.logo
          }
        }
      }
    }
    return Object.assign(orderFactory.convertOrder(this.order), { store })
  }

  /** 订单子单退款 */
  get orderSubPaymentRefund () {
    const { subList, paymentRefundList } = this.viewOrder
    const result = {
      price: 0,
      state: false,
    }
    if (subList && subList.length) {
      each(subList, sub => {
        if (sub.paymentRefundList && sub.paymentRefundList.length) {
          result.state = true
        }
        result.price += sumBy(sub.paymentRefundList || [], item => item.amountRefunded) || 0
      })
    }
    if (paymentRefundList && paymentRefundList.length) {
      result.state = true
      result.price += sumBy(paymentRefundList || [], item => item.amountRefunded) || 0
    }
    return result
  }

  /** 加工后的详情列表数据 */
  get viewDetailList (): IViewOrderDetail[] {
    if (this.order.ordersType === 'dc' || this.order.ordersType === 'mall') {
      return orderFactory.convertDetail(this.order)
    } else {
      return orderFactory.virtualDetail(this.order)
    }
  }

  get viewOrderBoxPrice () {
    return (this.order.detailList || []).filter((item) => item.itemType === 'box')
  }

  /** 根据detailId返回detail项 */
  get detailIdToViewDetail (): { [detailId: string]: IViewOrderDetail } {
    return keyBy(this.viewDetailList, 'detailId')
  }

  /** 根据pk返回detail项 */
  get pkToViewDetail (): { [pk: string]: IViewOrderDetail } {
    return keyBy(this.viewDetailList, 'pk')
  }

  /** map */
  get viewDetailMap () {
    const detailMap: any = {}
    // const memberIdArr = []
    this.viewDetailList.forEach(item => {
      const box = (detailMap[item.addUserId]?.box || 0) + item.totalBoxPrice
      // const express = orderMobx.expressMap?.[item.addUserId] || 0
      // const share = box + express
      const totalPriceWithTax = (detailMap[item.addUserId]?.totalPriceWithTax || 0) + item.totalPriceWithTax
      if (detailMap[item.addUserId]) {
        const offPrice = detailMap[item.addUserId].offPrice + item.offPrice
        detailMap[item.addUserId].box = box
        // detailMap[item.addUserId].share = share
        detailMap[item.addUserId].totalPriceWithTax = totalPriceWithTax
        detailMap[item.addUserId].offPrice = detailMap[item.addUserId].offPrice + item.offPrice
        detailMap[item.addUserId].needPayNoExpress = totalPriceWithTax - offPrice + box
        detailMap[item.addUserId].products?.push(item)
      } else {
        detailMap[item.addUserId] = {
          addUserId: item.addUserId,
          addUserName: item.addUserName,
          box,
          offPrice: item.offPrice,
          totalPriceWithTax: item.totalPriceWithTax,
          needPayNoExpress: item.totalPriceWithTax - item.offPrice + box,
          products: [item],
          avatarUrl: ''
        }
      }
      // if (memberIdArr.indexOf(item.addUserId) === -1) {
      //   memberIdArr.push(item.addUserId)
      // }
    })

    // if (memberIdArr.length) {
    //   buyTogetherMobx.getBulkAvatars(memberIdArr)
    // }
    return detailMap
  }

  /** pos 登录会员信息 */
  get posMember () {
    let { memberId = '0', memberGroupId = '0' } = this.viewOrder.posMember
    // 先付费会员自动登录
    if (!this.viewOrder.payMode || (this.viewOrder.payMode && !this.viewOrder.tableCode)) {
      memberId = this.viewOrder.memberId
      memberGroupId = this.viewOrder.memberGroupId
    }
    return { memberId: memberId?.toString(), memberGroupId: Number(memberGroupId) }
  }

  /** 云端购物车项 pk map */
  get ordersCartMap () {
    const items: ISaveCartItem[] = []
    this.order.ordersCart?.forEach(item => {
      items.push(item)
      item.huangou?.forEach(h => {
        items.push(h)
      })
      item.zengsong?.forEach(z => {
        items.push(z)
      })
      item.productGroups?.forEach(g => {
        g.productGroupDetails?.forEach(d => {
          items.push(d)
        })
      })
    })
    return keyBy(items, 'pk')
  }

  /** 待处理退款申请 */
  get ordersRefundPending () {
    return this.order?.ordersRefundList?.find(i => i?.status === 'Pending')
  }

  /** 承接多方来源，当前使用单例方案，TODO 可能改成工厂模型 */
  async init (ordersId: string, config?: {
    member?: boolean;
    deliveryAndInvoice?: boolean;
    source?: 'cart' | 'order' | 'printer';
    isNoToken?: boolean;
    showOrderCart?: boolean;
    memberId?: number;
    fromGoldPlan?: boolean;
    brandId?: number;
    showMember?: 0 | 1;
    showMktProducts?: 0 | 1;
    showPingtanByMember?: 0 | 1; // 拼单业务打包费及配送费分摊
    isBuyTogether?: boolean;
    isCancel?: boolean;
    showOrdersLog?: 0 | 1;
  }) {
    let order: any
    if (ordersId) {
      this.doOrder.isLoading = true
      // 如果购物车的订单过来时 判断是否用缓存数据
      if (config?.source === 'cart' &&
        this.viewOrder.ordersId === ordersId &&
        this.viewDetailList.length &&
        (Date.now() - this.doOrder.fetchTime) <= 60000) {
        return
      } else {
        this.doOrder.fetchTime = Date.now()
        order = await orderFactory.create(ordersId, {
          member: config?.member,
          isNoToken: config?.isNoToken,
          deliveryAndInvoice: config?.deliveryAndInvoice,
          showOrderCart: config?.showOrderCart,
          memberId: config?.memberId,
          fromGoldPlan: config?.fromGoldPlan,
          brandId: config?.brandId,
          showMember: config?.showMember,
          showMktProducts: config?.showMktProducts,
          isCancel: config?.isCancel,
          showPingtanByMember: config?.showPingtanByMember || 0,
          showOrdersLog: config?.showOrdersLog || 0,
        })
      }
    }
    runInAction(() => {
      this.doOrder.isLoading = false
      this.order = order.data
      if (config?.isBuyTogether) {
        this.getExpressMap(order.data)
      }
    })
    return this.order
  }

  /** 初始化数据 */
  initToDetail (ordersData: any) {
    this.order = cloneDeep(ordersData)
    return this.order
  }

  /** 批量充值订单 */
  initBatchRechargeOrder (record: any) {
    this.batchRechargeOrder = record
  }

  /** 预订单 */
  async initPreOrderData (ordersData: any) {
    let order: any
    if (ordersData) {
      order = await orderFactory.createPreOrderData(ordersData)
    }
    runInAction(() => {
      this.order = order.data
    })
    return this.order
  }

  /** 重置 */
  reset () {
    this.order = getDefaultOrder()
    this.doOrder.isLoading = false
    this.doOrder.isSim = false
    this.doOrder.fetchTime = 0
  }

  /** cancel订单（未支付) */
  async cancel (manual = true, cancelReason?: string) {
    if (this.order.ordersId) {
      return OrderAPI.cancel({
        addUserId: restaurantConfig.managerId,
        addUserName: restaurantConfig.managerName,
        ordersId: this.order.ordersId,
        cancelReason: cancelReason || 'no reason',
        memberId: restaurantConfig.app === 'mobile' ? restaurantConfig?.member?.id : undefined
      }).then(() => {
        if (manual) {
          runInAction(() => {
            this.order.status = 'Cancel'
          })
          restaurantCenter.emit(RESTAURANT_EVENT.CANCEL_ORDER, {
            order: {
              ordersId: this.order.ordersId
            }
          })
          return true
        } else {
          return true
        }
      }, (err: any) => {
        throw err
      })
    } else {
      return Promise.reject('no order')
    }
  }

  /** void订单(已支付) */
  async void () {
  }

  /** 用于下单后在回调中置入下单的订单的实际数据 */
  restore (order: IOrder, isRestoreDiscount = false) {
    /** order 订单数据, isRestoreDiscount:是否前端去还原优惠数据 */
    let originalDiscountList: any = []
    // 退菜和拆单的时候,优惠数据缓存
    if (isRestoreDiscount) {
      originalDiscountList = cloneDeep(this.order.discountList)
    }

    this.order = order
    if (isRestoreDiscount && originalDiscountList && originalDiscountList.length > 0) {
      this.order.discountList = originalDiscountList
    }
    this.doOrder.fetchTime = Date.now()
    return this.order
  }

  /** 返回一个order当前的模型 */
  get () {
    return {
      ...this.viewOrder,
      detail: this.viewDetailList
    }
  }

  /** 修改订单信息 */
  update (info: IUpdateInfo) {
    // TODO 异常的处理
    if (restaurantConfig.employeeInfo?.employeeName) {
      info.addUserName = restaurantConfig.employeeInfo?.employeeName
    }
    return OrderAPI.update(info)
  }

  /** 修改桌号 */
  updateTable (info: IUpdateInfo) {
    // TODO 异常的处理
    if (restaurantConfig.employeeInfo?.employeeName) {
      info.addUserName = restaurantConfig.employeeInfo?.employeeName
    }
    return OrderAPI.updateTable(info)
  }

  /** 录入桌号 */
  createTable (info: IUpdateInfo) {
    // TODO 异常的处理
    if (restaurantConfig.employeeInfo?.employeeName) {
      info.addUserName = restaurantConfig.employeeInfo?.employeeName
    }
    return OrderAPI.createTable(info)
  }

  /** 锁死状态修改 */
  setSleep (bo: boolean) {
    this.$sleep = bo
  }

  /** 转台 */
  setChangeTablesWithOrders (params: { ordersId: string; tableCode?: string; tableAlias?: string; ordersIdB?: string; addUserName?: string }) {
    return OrderAPI.postChangeTableWithOrders(params)
  }

  /** 退菜 */
  async cancelProduct (detail: { [detailId: string]: number }, reason: string) {
    const ordersId = this.order.ordersId
    if (ordersId) {
      const res: any = await OrderAPI.reduce({
        details: detail,
        refundReason: reason,
        ordersId: this.order.ordersId,
        addUserId: restaurantConfig.managerId,
        addUserName: restaurantConfig.managerName
      })
      if (res?.result) await this.restore(res.result, true)
      if (res?.result && ordersId) cartMobx.syncMkt(true)
      return res
    } else {
      return Promise.reject()
    }
  }

  /** 拆菜 */
  async splitProduct (orderItem: IViewOrderDetail, splitCnt?: number) {
    const ordersId = this.order.ordersId
    if (ordersId) {
      const splitArr = splitCnt ? [splitCnt, orderItem.cnt - splitCnt] : (new Array(orderItem.cnt)).fill(1)
      const res = await (OrderAPI as any).splitProduct({
        ordersId,
        addUserId: restaurantConfig.managerId,
        addUserName: restaurantConfig.managerName,
      }, {
        [orderItem.detailId]: splitArr,
      })
      if (res?.result) await this.restore(res.result, true)
      if (res?.result && ordersId) await cartMobx.syncMkt(true)
      return res
    } else {
      return Promise.reject()
    }
  }

  /** 更新支付单信息 */
  updatePaymentList (paymentList: any[]) {
    this.order.paymentList = paymentList
  }

  updateTip (tip: number) {
    this.order.tip = tip
    this.order.isManualTip = true
  }

  /** 发起支付，将锁定订单，将订单状态变更为paying */
  async startPay (ordersId?: string, paidFee?: number): Promise<IPrepayOrder> {
    if (this.$sleep) {
      return Promise.reject('$sleep')
    } else {
      ordersId = ordersId || this.order.ordersId
      if (!ordersId) {
        return Promise.reject(false)
      } else {
        this.$sleep = true
        setTimeout(() => {
          runInAction(() => {
            console.log('支付startPay防抖释放 ordersId:', ordersId)
            this.$sleep = false
          })
        }, 2000)
        return OrderAPI.startPay({
          addUserId: restaurantConfig.managerId,
          addUserName: restaurantConfig.managerName,
          ordersId,
          clientNumber: restaurantConfig.deviceId || '',
          paidFee: paidFee || this.order.paidFee
        }).then((res: any) => {
          if (res && res.result) {
            return res.result
          } else {
            throw new Error(res)
          }
        })
      }
    }
  }

  /** 三方支付专用，可以直接将订单重新变为支付状态 */
  async payToSucceed () {
    if (this.order.ordersId && this.order.channel === '3rdDelivery') {
      return (OrderAPI as any).thirdPay({
        ordersId: this.order.ordersId,
        addUserId: restaurantConfig.managerId,
        addUserName: restaurantConfig.managerName,
      }).then((res: any) => {
        runInAction(() => {
          this.restore(res.result)
        })
      })
    } else {
      return false
    }
  }

  /** 更新餐品 startTime */
  async updateItemsStartTime (delayMap: { [pk: string]: number }) {
    const delayDetailMapToStartTime: { [pk: string]: string } = {}
    this.order.detailList.forEach(detail => {
      // 如果detail上没有设置过offset，则认为并不是一个延迟餐品不用额外处理
      // TODO 当前手机点餐的预约点餐同时跟POS上延迟打印类似，但是并不存在startTimeOffset，所以使用订单中的startTime作为判断依据
      const isReservationOrder = this.order.startTime !== this.order.addTime
      if (['product', 'FixedCombo', 'OptionalCombo'].includes(detail.itemType) && delayMap[detail.pk] !== undefined && (detail.startTimeOffset || isReservationOrder)) {
        delayDetailMapToStartTime[detail.detailId] = ddayjs().add(delayMap[detail.pk], 'minute').format()
      }
    })
    return (OrderAPI as any).updateDetailStartTime(delayDetailMapToStartTime, {
      ordersId: this.order.ordersId,
      addUserId: restaurantConfig.managerId,
      addUserName: restaurantConfig.managerName,
    }).then((res: any) => {
      const detailMap = keyBy(res?.detailList, 'detailId')
      runInAction(() => {
        this.order.detailList.forEach(detail => {
          if (delayDetailMapToStartTime[detail.detailId]) {
            detail.startTime = detailMap?.[detail.detailId]?.startTime || delayDetailMapToStartTime[detail.detailId]
            detail.kitchenPrintTime = detailMap?.[detail.detailId]?.kitchenPrintTime || delayDetailMapToStartTime[detail.detailId]
          }
        })
      })
    })
  }

  /** 发短信 */
  sendMsgToCustom = (orderId: string, value: string, type: 'mobile' | 'email') => {
    return OrderAPI.sendMsg(orderId, value, type)
  }

  /** 删除购物车 */
  delTempCart () {
    if (this.order.ordersId) {
      return OrderAPI.delCart(this.order.ordersId).then(() => {
        // 当前只会返回成功的状态
        runInAction(() => {
          delete this.order.ordersCart
          this.order.status = 'Pending'
        })
        restaurantCenter.emit(RESTAURANT_EVENT.ORDER_CART_DELETED, {
          order: {
            ordersId: this.order.ordersId
          }
        })
      })
    } else {
      return Promise.reject(false)
    }
  }

  /** 更改订单状态 */
  setStatus (status: TOrderStatus) {
    this.order.status = status
  }

  /** 更新小票时间 */
  updateGuestReceiptTime () {
    if (this.order.ordersId) {
      return (OrderAPI as any).updatePrePayPrintTime({
        ordersId: this.order.ordersId,
        addUserId: restaurantConfig.managerId,
        addUserName: restaurantConfig.managerName,
      }).then(() => {
        // console.log('### res', res)
        // 当前只会返回成功的状态
        // runInAction(() => {
        //   delete this.order.ordersCart
        //   this.order.status = 'Pending'
        // })
      })
    } else {
      return Promise.reject(false)
    }
  }

  /** 重打小票 */
  reprint (ordersId: string, ticketType: string, extInfo?: { printerId?: string; paymentId?: number; receiptType?: string; autoPrint?: boolean; data?: any }) {
    return (OrderAPI as any).rePrint({
      addUserId: restaurantConfig.managerId,
      addUserName: restaurantConfig.managerName,
      ordersId
    }, ticketType as any, extInfo)
  }

  /** 设置分单子单信息 */
  setSplitInfo (ordersId: string) {
    this.splitInfo = {
      subOrdersId: ordersId
    }
  }

  /** 检查消费送券 */
  checkConsumeSendCoupon (ordersId: string) {
    return OrderAPI.checkConsumeSendCoupon({ storeId: storeMobx.info.storeId, ordersId })
  }

  /** 获取配送费 map */
  getExpressMap (order?: any) {
    (order || this.order).detailList.forEach((detail: any) => {
      if (detail.itemType === 'express') {
        this.expressMap[detail.addUserId] = (this.expressMap[detail.addUserId] || 0) + detail.paidFee
      }
    })
    return this.expressMap
  }

  /** 订单绑定会员 */
  async bindMemberOnOrder (params: { ordersId: string; memberId: string; memberGroupId: string }) {
    const res = await (OrderAPI as any).bindMemberOnOrders({ ...params })
    runInAction(() => {
      res?.result && this.restore(res?.result)
    })
  }
}

export default new Order()
